import { useState, useEffect, useMemo, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { getCRMSegments, deleteCRMSegment } from '../api';
import { DashboardPage, TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import { SummaryCard, METRICS_GRID } from '../components/CRMDashboardMetrics';
import { Button, ConfirmDeleteModal, SearchInput } from '@/components';
import { PlusIcon } from '../../components/Icons';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import sendIcon from '../../assets/Send.png';
import { CRMSegmentModal } from './../components/CRMSegmentModal';
import { SegmentPushFlow } from './../components/SegmentPushFlow';
import { parseApiError } from '@/common/utils';
import { usePermissions } from '@/common/hooks/usePermissions';
import { useDelayedSkeleton } from '@/common/hooks';
import { PERMISSIONS } from '@/common/config/permissions';
import peopleIcon from '../../assets/people.png';
import activeContactIcon from '../../assets/activeContact.png';

export function CRMSegmentsPage() {
  const { can } = usePermissions();
  const canCreateGroups = can(PERMISSIONS.SEGMENTS.CREATE);
  const canUpdateGroups = can(PERMISSIONS.SEGMENTS.UPDATE);
  const canDeleteGroups = can(PERMISSIONS.SEGMENTS.DELETE);
  const canPushCampaign = can(PERMISSIONS.WA_CAMPAIGNS.CREATE);

  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSegmentId, setEditingSegmentId] = useState(null);
  const [pushSegment, setPushSegment] = useState(null);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isInitialSearchMount = useRef(true);

  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (isInitialSearchMount.current) {
      isInitialSearchMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: segmentsData,
    isLoading: isInitialLoading,
    isFetching,
  } = useQuery({
    queryKey: ['crm-segments', currentCompanyId, page, debouncedSearch],
    queryFn: () =>
      getCRMSegments(currentCompanyId, {
        page,
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      }),
    enabled: !!currentCompanyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && segmentsData !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  const { mutate: handleDeleteSegment, isPending: isDeleting } = useMutation({
    mutationFn: (segmentId) => {
      if (!canDeleteGroups) throw new Error('Permission denied.');
      return deleteCRMSegment({ companyId: currentCompanyId, segmentId });
    },
    onSuccess: () => {
      toast.success('Group deleted successfully!');
      queryClient.invalidateQueries({
        queryKey: ['crm-segments', currentCompanyId],
      });
      setSegmentToDelete(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete Group.');
      setSegmentToDelete(null);
    },
  });

  const segments = segmentsData?.results || [];
  const totalSegments = segmentsData?.count || segments.length;

  // Calculate total contacts matched across all segments (if needed) or keep 0 for now.
  const totalContacts = segments.reduce(
    (sum, seg) => sum + (seg.contact_count || 0),
    0,
  );

  const segmentColumns = useMemo(() => {
    const cols = [
      {
        id: 'name',
        label: 'Group Name',
        defaultWidth: 180,
        minWidth: 140,
        cell: (segment) => (
          <span className="truncate block font-medium" title={segment.name}>
            {segment.name}
          </span>
        ),
      },
      {
        id: 'description',
        label: 'Description',
        defaultWidth: 240,
        minWidth: 160,
        cell: (segment) => (
          <span className="truncate block" title={segment.description || ''}>
            {segment.description || '-'}
          </span>
        ),
      },
      {
        id: 'contacts',
        label: 'Matched Contacts',
        defaultWidth: 150,
        minWidth: 120,
        cell: (segment) => segment.contact_count,
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 120,
        minWidth: 100,
        cell: () => <TableChip tone="success">Active</TableChip>,
      },
      {
        id: 'evaluated',
        label: 'Last Evaluated',
        defaultWidth: 160,
        minWidth: 130,
        cell: (segment) =>
          segment.last_evaluated_at
            ? formatDistanceToNow(new Date(segment.last_evaluated_at), {
                addSuffix: true,
              })
            : 'Never',
      },
    ];
    if (canPushCampaign || canUpdateGroups || canDeleteGroups) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        defaultWidth: 140,
        minWidth: 120,
        align: 'center',
        stopPropagation: true,
        cell: (segment) => (
          <div className="flex justify-center gap-3.5 items-center">
            {canPushCampaign && (
              <button
                type="button"
                className="text-text-muted hover:opacity-80 transition-opacity shrink-0"
                onClick={() => {
                  const count = Number(segment.contact_count) || 0;
                  if (count <= 0) {
                    toast.error(
                      'No WhatsApp recipients. Contacts need opt-in consent and a phone number.',
                    );
                    return;
                  }
                  setPushSegment(segment);
                }}
                title="Send WhatsApp campaign"
                aria-label={`Send WhatsApp campaign to ${segment.name}`}
              >
                <img
                  src={sendIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
            {canUpdateGroups && (
              <button
                type="button"
                className="text-text-muted hover:text-text-primary transition-colors shrink-0"
                onClick={() => {
                  setEditingSegmentId(segment.id);
                  setIsModalOpen(true);
                }}
                title="Edit Group"
              >
                <img
                  src={editIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
            {canDeleteGroups && (
              <button
                type="button"
                className="hover:opacity-80 transition-opacity shrink-0"
                onClick={() => setSegmentToDelete(segment)}
                title="Delete Group"
              >
                <img
                  src={trashIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
          </div>
        ),
      });
    }
    return cols;
  }, [canPushCampaign, canUpdateGroups, canDeleteGroups]);

  if (pushSegment) {
    return (
      <DashboardPage className="!bg-transparent !p-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
        <SegmentPushFlow
          companyId={currentCompanyId}
          segment={pushSegment}
          onClose={() => setPushSegment(null)}
        />
      </DashboardPage>
    );
  }

  if (isModalOpen) {
    return (
      <DashboardPage className="!bg-transparent !p-0">
        <CRMSegmentModal
          companyId={currentCompanyId}
          segmentId={editingSegmentId}
          isReadOnly={editingSegmentId ? !canUpdateGroups : !canCreateGroups}
          onClose={() => {
            setIsModalOpen(false);
            setEditingSegmentId(null);
          }}
          onStartPush={
            canPushCampaign
              ? (segment) => {
                  setIsModalOpen(false);
                  setEditingSegmentId(null);
                  setPushSegment(segment);
                }
              : undefined
          }
        />
      </DashboardPage>
    );
  }

  return (
    <DashboardPage className="!bg-transparent !p-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="groups-page w-full flex flex-col gap-4 flex-1 min-h-0">
        {/* Metrics Section */}
        <div className={`${METRICS_GRID} shrink-0`}>
          <SummaryCard
            title="Total Group"
            value={totalSegments.toString()}
            iconSrc={peopleIcon}
            accent="#EEB90B"
            iconBg="rgba(238, 185, 11, 0.12)"
          />
          <SummaryCard
            title="Active Contacts"
            value={totalContacts.toLocaleString()}
            iconSrc={activeContactIcon}
            accent="#50AB3D"
            iconBg="rgba(80, 171, 61, 0.12)"
          />
        </div>

        {/* Segments List Section */}
        <div className="groups-list-card w-full rounded-2xl bg-white border border-border-default p-4 flex flex-col gap-4 flex-1 min-h-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search groups by name..."
              className="w-full sm:max-w-md sm:flex-1 order-2 sm:order-1"
              inputClassName="h-[44px]"
            />
            {canCreateGroups && (
              <Button
                variant="primary"
                className="h-[44px] px-5 flex items-center justify-center gap-2 font-bold transition-all order-1 sm:order-2 w-full sm:w-auto"
                onClick={() => {
                  setEditingSegmentId(null);
                  setIsModalOpen(true);
                }}
                icon={<PlusIcon className="w-5 h-5" />}
              >
                <span>Create Group</span>
              </Button>
            )}
          </div>

          <div className="w-full flex flex-col flex-1 min-h-0">
            <V2Table
              tight
              className="groups-table"
              storageKey="crm_segments_table_column_widths"
              columns={segmentColumns}
              data={isLoading || !segmentsData ? [] : segments}
              rowKey="id"
              loading={isLoading || !segmentsData}
              loadingRowCount={10}
              onRowClick={(segment) => {
                setEditingSegmentId(segment.id);
                setIsModalOpen(true);
              }}
              empty="No groups found."
              pagination={{
                currentPage: page,
                totalCount: totalSegments,
                itemsPerPage: 10,
                onChange: setPage,
              }}
            />
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={!!segmentToDelete}
        onCancel={() => setSegmentToDelete(null)}
        onConfirm={() => handleDeleteSegment(segmentToDelete.id)}
        title="Delete Group"
        description={`Are you sure you want to delete the Group "${segmentToDelete?.name}"? This action cannot be undone.`}
        isPending={isDeleting}
      />
    </DashboardPage>
  );
}
